import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-styled-flexboxgrid';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import { bool, object } from 'prop-types';
import styled, { css } from 'styled-components';
import { useAppState } from '../../../context/app-state.context';
import { usePressPageState } from '../../../context/press-page-state.context';
import { useStoryblokDatasourceEntries } from '../../../hooks';
import Chevron from '../../../icons/arrow-DCerulean.svg';
import { bodySRegularBrownStyles } from '../../../styles/typography';
import { bpWidth, colors } from '../../../styles/variables';
import Pagination from '../../Pagination/Pagination';
import FilterDropdown from './FilterDropdown';

const FilterRow = styled(Row)`
  background-color: ${colors.beige100};
  width: 100%;
  padding-bottom: 30px;

  &.is-sticky {
    position: sticky;
    z-index: 4;
    top: ${({ tabHeaderRowHeight }) => `${tabHeaderRowHeight - 1}px`};
    transition: transform 0.5s;
  }

  ${({ tabHeaderRowHeight, headerHeight, translateY }) => {
    let yValue;
    if (translateY < 0) {
      yValue = 0;
    } else if (translateY > 1) {
      yValue = 1;
    } else {
      yValue = translateY;
    }

    yValue = (yValue || 0) * headerHeight * -1;

    return css`
      &.is-pinned {
        top: ${headerHeight + tabHeaderRowHeight - 1}px;
        transform: translate3d(0, ${yValue}px, 0);
      }
    `;
  }};
`;

const SortButtonCol = styled(Col)`
  margin-top: 50px;
`;

const SortButton = styled.button`
  cursor: pointer;
  ${bodySRegularBrownStyles};
  background: transparent;
  text-align: start;
  border: none;
`;

const SortButtonRow = styled(Row)`
  svg {
    max-width: 12px;
    height: 6px;
    margin-right: 0.2em;

    path#Path_2 {
      stroke: ${(props) => (props.isActive ? colors.cerulean500 : colors.beige700)} !important;
    }
  }

  ${SortButton} {
    color: ${(props) => (props.isActive ? colors.gray700 : colors.beige700)} !important;
  }
`;

const DescRow = styled(SortButtonRow)`
  svg {
    transform: rotate(180deg);
  }
`;

const AscRow = styled(SortButtonRow)``;

const PostWrapper = styled(Col)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 20px;

  @media (${bpWidth.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (${bpWidth.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const PaginationCol = styled(Col)`
  padding: 0;
  margin: 70px auto;
`;

const PostGrid = ({ blok, stickyTop }) => {
  const categories = useStoryblokDatasourceEntries('press-category');
  const { headerHeight, headerTranslateY } = useAppState();
  const { tabHeaderRowRef, filterRowRef } = usePressPageState();
  const [orderBy, setOrderBy] = useState('desc');
  const [sortedPosts, setSortedPosts] = useState([...(blok.list || [])]);
  const [paginationClass, setPaginationClass] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [postsPerPage, setPostsPerPage] = useState(6);
  const [postsShown, setPostsShown] = useState(postsPerPage);
  const [posts, setPosts] = useState(sortedPosts.slice(0, postsPerPage));
  const [totalPosts, setTotalPosts] = useState(sortedPosts.length);
  const isDesc = useMemo(() => orderBy === 'desc', [orderBy]);
  const isAsc = useMemo(() => orderBy === 'asc', [orderBy]);
  const tabHeaderRowHeight = useMemo(
    () => tabHeaderRowRef?.current?.clientHeight || 0,
    [tabHeaderRowRef?.current?.clientHeight],
  );
  const filterRowOffsetTop = useMemo(() => filterRowRef?.current?.offsetTop || 0, [filterRowRef?.current?.offsetTop]);
  const filterRowCssClass = useMemo(
    () =>
      [stickyTop ? 'is-sticky' : undefined, filterRowOffsetTop > tabHeaderRowHeight ? 'is-pinned' : undefined]
        .filter(Boolean)
        .join(' '),
    [stickyTop, tabHeaderRowHeight, filterRowOffsetTop],
  );

  const handleSort = (_posts) => {
    switch (orderBy) {
      case 'desc':
        _posts.sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
        break;
      case 'asc':
        _posts.sort((a, b) => new Date(a.publishDate).getTime() - new Date(b.publishDate).getTime());
        break;
      default:
    }

    return _posts;
  };

  useEffect(() => {
    const sorted = handleSort([...sortedPosts]);
    setSortedPosts(sorted);
    setPostsShown(postsPerPage);
  }, [orderBy]);

  useEffect(() => {
    setPosts(sortedPosts.slice(0, postsPerPage));
    setTotalPosts(sortedPosts.length);
  }, [sortedPosts]);

  useEffect(() => {
    if (postsShown === 0) {
      setPostsShown(postsPerPage);
    }
  }, []);

  useEffect(() => {
    if (postsShown > postsPerPage && postsShown < totalPosts) {
      setPaginationClass('bothCtas');
    } else if (postsShown > postsPerPage && postsShown >= totalPosts) {
      setPaginationClass('prevCtaOnly');
    } else if (postsShown <= postsPerPage && postsShown < totalPosts) {
      setPaginationClass('nextCtaOnly');
    }
  }, [postsShown]);

  const handleFilterChange = (value) => {
    let filteredData = [...(blok.list || [])];
    if (value !== 'all') {
      filteredData = filteredData.filter((p) => p.category === value);
    }
    const sorted = handleSort(filteredData);
    setSortedPosts(sorted);
    setPostsShown(postsPerPage);
  };

  const previousPosts = () => {
    if (postsShown > postsPerPage) {
      if (postsShown - postsPerPage > 0 && totalPosts !== postsShown) {
        setPosts(sortedPosts.slice(postsShown - 2 * postsPerPage, postsShown - postsPerPage));
        setPostsShown(postsShown - postsPerPage);
      } else if (totalPosts === postsShown) {
        if (totalPosts % postsPerPage !== 0) {
          const remainingPosts = totalPosts % postsPerPage;
          setPosts(sortedPosts.slice(postsShown - postsPerPage - remainingPosts, postsShown - remainingPosts));
          setPostsShown(postsShown - remainingPosts);
        } else {
          setPosts(sortedPosts.slice(postsShown - 2 * postsPerPage, postsShown - postsPerPage));
          setPostsShown(postsShown - postsPerPage);
        }
      }
    }
  };

  const nextPosts = () => {
    if (postsShown < totalPosts) {
      const remainder = totalPosts - postsShown;
      if (remainder > postsPerPage) {
        setPosts(sortedPosts.slice(postsShown, postsShown + postsPerPage));
        setPostsShown(postsShown + postsPerPage);
      } else {
        const remainingPosts = postsShown + (totalPosts - postsShown);
        setPosts(sortedPosts.slice(postsShown, remainingPosts));
        setPostsShown(remainingPosts);
      }
    }
  };

  return (
    <Col {...storyblokEditable(blok)} xs={8} md={10} lg={12}>
      <FilterRow
        tabHeaderRowHeight={tabHeaderRowHeight}
        headerHeight={headerHeight}
        translateY={headerTranslateY}
        className={filterRowCssClass}
        ref={filterRowRef}
      >
        <FilterDropdown optionList={categories} stickyTop={stickyTop} onFilter={handleFilterChange} />
        <SortButtonCol xsOffset={1} mdOffset={0}>
          <DescRow isActive={isDesc}>
            <Col className="flex items-center">
              <Chevron />
            </Col>
            <Col>
              <SortButton onClick={() => setOrderBy('desc')}>Most recent</SortButton>
            </Col>
          </DescRow>
          <AscRow isActive={isAsc}>
            <Col className="flex items-center">
              <Chevron />
            </Col>
            <Col>
              <SortButton onClick={() => setOrderBy('asc')}>Oldest</SortButton>
            </Col>
          </AscRow>
        </SortButtonCol>
      </FilterRow>
      <Row>
        <PostWrapper xs={8} md={10} lg={12}>
          {posts.map((nestedBlok) => (
            <Row key={nestedBlok._uid}>
              <StoryblokComponent blok={nestedBlok} />
            </Row>
          ))}
        </PostWrapper>
      </Row>
      <Row>
        <PaginationCol xs={8} md={10} lg={12}>
          <Row>
            <Pagination
              paginationClass={paginationClass}
              postsShown={postsShown}
              postsPerPage={postsPerPage}
              totalPosts={totalPosts}
              previousPosts={previousPosts}
              nextPosts={nextPosts}
            />
          </Row>
        </PaginationCol>
      </Row>
    </Col>
  );
};

PostGrid.propTypes = {
  blok: object.isRequired,
  stickyTop: bool,
};

export default PostGrid;
